/**
 * TenantUserLanding
 *
 * Default landing page for regular tenant users.
 * Defines the PATTERN for per-functional-area dashboards (WQ-087).
 *
 * WQ-086: Tenant Dashboard Architecture (Tier 3 — reference implementation)
 */

import React from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useAdminContext, type TenantContext } from '../../../contexts/AdminContextProvider';
import { resolveIcon } from '../../../components/common/IconResolver';

interface TenantUserLandingProps {
  tenant?: TenantContext;
}

// ---------------------------------------------------------------------------
// KPI Card (pattern component — WQ-087 reuses)
// ---------------------------------------------------------------------------

interface KPICardProps {
  label: string;
  value: string | number;
  icon: string;
  subtitle?: string;
  accentColor?: string;
}

const KPICard: React.FC<KPICardProps> = ({
  label, value, icon, subtitle, accentColor = 'var(--accent-primary, #7c3aed)',
}) => (
  <div className="card stat-card card-elevated">
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3, 12px)' }}>
      <div style={{
        width: '48px',
        height: '48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--accent-primary-muted, rgba(124, 58, 237, 0.1))',
        borderRadius: 'var(--radius-lg, 12px)',
        color: accentColor,
        flexShrink: 0,
      }}>
        {resolveIcon(icon, 24)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="stat-value" style={{ fontSize: 'var(--text-2xl, 24px)' }}>{value}</div>
        <div className="stat-label">{label}</div>
        {subtitle && (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Quick Action Button (pattern component — WQ-087 reuses)
// ---------------------------------------------------------------------------

interface QuickActionProps {
  label: string;
  icon: string;
  tab: string;
}

const QuickAction: React.FC<QuickActionProps> = ({ label, icon, tab }) => {
  const handleClick = () => {
    window.dispatchEvent(new CustomEvent('admin-navigate', { detail: { tab } }));
  };
  return (
    <button
      className="btn"
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 20px',
        background: 'var(--bg-secondary, rgba(255,255,255,0.05))',
        border: '1px solid var(--border-primary, rgba(255,255,255,0.1))',
        borderRadius: 'var(--radius-md, 8px)',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 500,
        transition: 'background 0.15s, border-color 0.15s',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover, rgba(255,255,255,0.08))';
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent-primary, #7c3aed)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.background = 'var(--bg-secondary, rgba(255,255,255,0.05))';
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-primary, rgba(255,255,255,0.1))';
      }}
    >
      {resolveIcon(icon, 18)}
      {label}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const TenantUserLanding: React.FC<TenantUserLandingProps> = ({ tenant: tenantProp }) => {
  const { user } = useAuth();
  const { activeTenant } = useAdminContext();
  const tenant = tenantProp || activeTenant;

  const userName = user?.name || 'User';
  const tenantName = tenant?.name || 'Your Organization';

  return (
    <div className="page-container">
      {/* Welcome Header */}
      <div className="page-header">
        <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {resolveIcon('home', 24)}
          Welcome back, {userName}
        </h2>
        <p style={{ color: 'var(--text-secondary)', marginTop: '4px', fontSize: '14px' }}>
          {tenantName}
        </p>
      </div>

      {/* KPI Cards — placeholder values, WQ-087 wires real data per functional area */}
      <div className="grid grid-cols-3" style={{ marginBottom: 'var(--space-6)' }}>
        <KPICard
          label="My Tasks"
          value="—"
          icon="check-circle"
          subtitle="No tasks assigned"
        />
        <KPICard
          label="Notifications"
          value="—"
          icon="bell"
          subtitle="No new notifications"
        />
        <KPICard
          label="Activity"
          value="—"
          icon="activity"
          subtitle="Recent activity"
        />
      </div>

      {/* Getting Started / Quick Actions */}
      <div className="card card-elevated" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
        <h3 style={{
          margin: '0 0 var(--space-4)',
          fontSize: 'var(--text-lg, 18px)',
          color: 'var(--text-primary)',
        }}>
          Quick Actions
        </h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <QuickAction label="Settings" icon="settings" tab="tenant-settings" />
          <QuickAction label="Chat" icon="message-circle" tab="chat" />
          <QuickAction label="Profile" icon="user" tab="profile" />
        </div>
      </div>

      {/* Info card */}
      <div className="card card-elevated" style={{
        padding: 'var(--space-4)',
        background: 'var(--bg-secondary, rgba(255,255,255,0.03))',
        borderLeft: '3px solid var(--accent-primary, #7c3aed)',
      }}>
        <h4 style={{ margin: '0 0 8px', fontSize: '14px', color: 'var(--text-primary)' }}>
          Your Workspace
        </h4>
        <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
          This is your personal dashboard. Functional area dashboards with real-time KPIs
          and work items will be available in a future update. Use the navigation to explore
          your organization's tools and settings.
        </p>
      </div>
    </div>
  );
};

export default TenantUserLanding;
